import { ReactNode, useState } from "react";
import { Button, Row, Segmented, Select } from "antd";
import { SortAscendingOutlined, SortDescendingOutlined } from "@ant-design/icons";
import Title from "antd/es/typography/Title";
import { ColumnType } from "antd/es/table";
import { buttonStyle, fullWidthStyle, titleStyle } from "../constants/style";
import Table from "./table";
import CardList from "./card-list";

type SizeLevel = 1 | 2 | 3 | 4 | 5;
type ViewMode = "card" | "table";

type Props<DataType> = {
  title?: string,
  columns: ColumnType<DataType>[],
  rows: DataType[],
  renderCard: (row: DataType) => ReactNode,
  storageKey: string,
  defaultViewMode?: ViewMode,
  emptyText?: string,
  loading?: boolean,
  disablePagination?: boolean,
  disableActionButton?: boolean,
  actionLabel?: string,
  titleSizeLevel?: SizeLevel,
  onAction?: boolean | (() => void),
  onSelect?: (r: DataType) => void,
  onContextMenu?: (r: DataType) => string[],
  onContextMenuOptionSelected?: (r: DataType, option: string) => void,
  sortOptions?: { label: string, value: string }[],
  sortField?: string,
  onSortFieldChange?: (value: string) => void,
  sortDescending?: boolean,
  onSortDescendingChange?: (value: boolean) => void
};

function getStoredViewMode(storageKey: string, defaultViewMode: ViewMode): ViewMode {
  const storedViewMode = localStorage.getItem(storageKey);
  return storedViewMode === "card" || storedViewMode === "table" ? storedViewMode : defaultViewMode;
}

export default function <DataType>(p: Props<DataType>) {
  const defaultViewMode = p.defaultViewMode ?? "card";
  const canAction = !(!p.onAction);
  const disableAction = p.disableActionButton ?? (!canAction || p.loading === true);
  const onAction = typeof p.onAction === "function" ? p.onAction as Function : undefined;
  const [viewMode, setViewMode] = useState<ViewMode>(() => getStoredViewMode(p.storageKey, defaultViewMode));

  function onViewModeChanged(newViewMode: ViewMode) {
    setViewMode(newViewMode);
    localStorage.setItem(p.storageKey, newViewMode);
  }

  return (
    <div style={fullWidthStyle}>
      <Row justify="space-between" align="middle">
        {
          !p.title ? undefined :
            <Title style={titleStyle} level={p.titleSizeLevel ?? 3}>{p.title}</Title>
        }
        <Row align="middle">
          <Segmented
            style={buttonStyle}
            value={viewMode}
            onChange={value => onViewModeChanged(value as ViewMode)}
            options={[
              { label: "Card", value: "card" },
              { label: "Table", value: "table" }
            ]}
          />
          {
            !p.sortOptions ? undefined :
              <Select
                style={buttonStyle}
                value={p.sortField}
                onChange={value => p.onSortFieldChange?.(value)}
                options={p.sortOptions}
              />
          }
          {
            !p.sortOptions ? undefined :
              <Button
                type="default"
                style={buttonStyle}
                icon={p.sortDescending ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
                onClick={() => p.onSortDescendingChange?.(!p.sortDescending)}
              />
          }
          {
            !canAction ? undefined :
              <Button type="primary" style={buttonStyle} disabled={disableAction} onClick={() => onAction?.()}>{p.actionLabel ?? "Create"}</Button>
          }
        </Row>
      </Row>
      {
        viewMode === "card" ?
          <CardList
            rows={p.rows}
            loading={p.loading}
            disablePagination={p.disablePagination}
            emptyText={p.emptyText}
            renderCard={p.renderCard}
            onSelect={p.onSelect}
            onContextMenu={p.onContextMenu}
            onContextMenuOptionSelected={p.onContextMenuOptionSelected}
          /> :
          <Table
            loading={p.loading ?? false}
            columns={p.columns}
            rows={p.rows}
            disablePagination={p.disablePagination}
            onSelect={p.onSelect}
            onContextMenu={p.onContextMenu}
            onContextMenuOptionSelected={p.onContextMenuOptionSelected}
          />
      }
    </div>
  );
}
